import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  Catalog,
  FetchCatalogListParams
} from 'Services/ProductManagement/Features/Catalog/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface CatalogListProps {
  rows: Catalog[]
  isLoading: boolean
  params: FetchCatalogListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  selectedCatalogId: string | undefined
  onRowClick: (id: string) => void
}

function CatalogList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  selectedCatalogId,
  onRowClick
}: Readonly<CatalogListProps>) {
  const headers: HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Sales channel name`,
      sortable: true
    },
    {
      id: 'product_ids',
      numeric: false,
      disablePadding: false,
      label: t`Products amount`,
      sortable: false
    },
    {
      id: 'lastModifiedTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Last modified`,
      sortable: true
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%' }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headers}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={selectedCatalogId === row.id}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {showValueOrHyphen(row.name)}
                    </ClickableTableCell>
                    <NoWrapTableCell>{row.product_ids.length}</NoWrapTableCell>
                    <NoWrapTableCell>
                      {datetimeFormatter(new Date(row.modified_timestamp))}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}

export default CatalogList
